import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type AbilitiesService from 'ember-can/services/abilities';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type { MessageType } from 'garaje/services/flash-messages';
import type MessageBusService from 'garaje/services/message-bus';
import type StateService from 'garaje/services/state';
import urlBuilder from 'garaje/utils/url-builder';
import { tracked } from 'tracked-built-ins';

/**
 * Payload delivered by emno-fe when the "Add rule" button is clicked in a
 * notification-settings section. Kept in sync with emno-fe:
 *   src/components/settings/utils/global-settings-add-rule-handoff.ts
 */
export interface AddRuleModalData {
  section: 'vips' | 'travelers' | 'home';
}

export type GlobalSettingsEvent = 'emnoGlobalSettingsAddRule' | 'closeModal' | 'showToaster';

export interface GlobalSettingsMessage {
  event: GlobalSettingsEvent;
  query?: { data?: AddRuleModalData };
  type?: MessageType;
  msg?: string;
}

export default class CommunicationsSettingsController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare state: StateService;
  @service declare messageBus: MessageBusService;
  @service declare flashMessages: FlashMessagesService;

  @tracked messageCategoriesSettingsExpanded: boolean = false;

  // EMNO-485: modal state driven by messages the emno-fe iframe posts. See
  // handleMessage for the three events (open / close / toast) — mirrors the
  // WPA scheduled-exports pattern in app/pods/analytics/scheduled-exports/.
  @tracked showModal = false;
  @tracked modalData: AddRuleModalData | Record<string, never> = {};

  constructor(properties: Record<string, unknown>) {
    super(properties);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.on('embedded-app-message', this, this.handleMessage);
  }

  willDestroy(): void {
    super.willDestroy();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.off('embedded-app-message', this, this.handleMessage);
  }

  // EMNO-475: once the home/VIPs or travelers split is on, this page renders the migrated
  // emno-fe settings iframe instead of the native "Message categories" panel. Gated via the
  // global-communication ability so the split check lives in one place.
  get useMigratedSettings(): boolean {
    return this.abilities.can('view-migrated-settings global-communications');
  }

  get iframeUrl(): string {
    return urlBuilder.emno.embeddedGlobalSettingsUrl(this.state.currentCompany.id);
  }

  get modalIframeUrl(): string {
    return urlBuilder.emno.embeddedGlobalSettingsAddRuleModalUrl(this.state.currentCompany.id);
  }

  /**
   * The iframe bridge. See app/components/embedded-app.gts — every framed
   * iframe rebroadcasts its messages on `embedded-app-message`, and this
   * controller listens for the ones scoped to the Add-Rule flow:
   *   - `emnoGlobalSettingsAddRule` — from the OUTER settings iframe: open the
   *     modal, forward `query.data` (section context) to the modal iframe via
   *     <EmbeddedApp @context>.
   *   - `closeModal` — from the MODAL iframe: shut the modal.
   *   - `showToaster` — from the MODAL iframe: flash a toast.
   * All other events on the bus are ignored (belong to other consumers).
   */
  @action
  handleMessage(message: GlobalSettingsMessage): void {
    if (message.event === 'emnoGlobalSettingsAddRule') {
      this.modalData = message.query?.data ?? {};
      this.showModal = true;
    } else if (message.event === 'closeModal') {
      this.showModal = false;
    } else if (message.event === 'showToaster') {
      const type = message.type ?? 'info';
      this.flashMessages.showAndHideFlash(type, message.msg ?? '');
    }
  }

  @action
  closeModal(): void {
    this.showModal = false;
  }
}
